import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Bell, Coffee, Sun, Moon as MoonIcon, Droplets, 
  Activity, Heart, Calendar, AlertCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import AppFooter from '@/components/AppFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RemindersSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const { data } = await axios.get(`${API}/reminders/preferences`, {
        withCredentials: true
      });
      setPreferences(data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Failed to load reminder settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (reminderType) => {
    setPreferences(prev => ({
      ...prev,
      [reminderType]: {
        ...prev[reminderType],
        enabled: !prev[reminderType].enabled
      }
    }));
  };

  const handleTimeChange = (reminderType, newTime) => {
    setPreferences(prev => ({
      ...prev,
      [reminderType]: {
        ...prev[reminderType],
        time: newTime
      }
    }));
  };

  const handleFrequencyChange = (reminderType, newFrequency) => {
    setPreferences(prev => ({
      ...prev,
      [reminderType]: {
        ...prev[reminderType],
        frequency: newFrequency
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/reminders/preferences`, preferences, {
        withCredentials: true
      });
      toast.success('Reminder settings saved successfully! 🎉');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const reminderTypes = [
    {
      key: 'breakfast',
      icon: Coffee,
      title: 'Breakfast Reminder',
      description: 'Get reminded to have a healthy breakfast',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      key: 'lunch',
      icon: Sun,
      title: 'Lunch Reminder',
      description: 'Time for a nutritious midday meal',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      key: 'dinner',
      icon: MoonIcon,
      title: 'Dinner Reminder',
      description: 'Wind down with a healthy dinner',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    },
    {
      key: 'water',
      icon: Droplets,
      title: 'Water Intake',
      description: 'Stay hydrated throughout the day',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      isMultiTime: true
    },
    {
      key: 'movement',
      icon: Activity,
      title: 'Movement Reminder',
      description: 'Get up and move your body',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      key: 'sleep',
      icon: MoonIcon,
      title: 'Sleep / Wind-down',
      description: 'Time to start your bedtime routine',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      key: 'wellness_checkin',
      icon: Heart,
      title: 'Wellness Check-in',
      description: 'Track your mood and wellness',
      color: 'text-pink-600',
      bgColor: 'bg-pink-50'
    },
    {
      key: 'health_logging',
      icon: Calendar,
      title: 'Health Logging',
      description: 'Log your preventive health metrics',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      isPeriodic: true
    },
    {
      key: 'annual_checkup',
      icon: AlertCircle,
      title: 'Annual Health Check',
      description: 'Schedule your preventive check-up',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      isPeriodic: true
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-healthloop-mint-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-healthloop-mint-50 via-healthloop-mint-100 to-healthloop-mint-200">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 md:top-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/profile')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Bell className="h-6 w-6 text-healthloop-mint-600" />
                  Health Reminders
                </h1>
                <p className="text-sm text-gray-600">Customize your reminder preferences</p>
              </div>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving || !preferences}
              className="bg-healthloop-mint-600 hover:bg-healthloop-mint-700"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24">
        {/* Info Card */}
        <Card className="mb-6 border-healthloop-mint-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-healthloop-mint-100 rounded-lg">
                <Bell className="h-5 w-5 text-healthloop-mint-700" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Stay Consistent with Smart Reminders</h3>
                <p className="text-sm text-gray-600">
                  Set up friendly reminders to help you build healthy habits. You can customize when and how often you receive each reminder. Don't worry - we'll keep them supportive and non-intrusive! 💚
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Show message if no preferences loaded */}
        {!preferences || Object.keys(preferences).length === 0 ? (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">No Reminder Preferences Found</h3>
              <p className="text-sm text-gray-600 mb-4">
                We couldn't load your reminder settings. This might be your first time here, or there was an issue loading your preferences.
              </p>
              <Button
                onClick={fetchPreferences}
                className="bg-healthloop-mint-600 hover:bg-healthloop-mint-700"
              >
                Retry Loading
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Reminder Cards */
          <div className="space-y-4">
            {reminderTypes.map((reminder) => {
              const Icon = reminder.icon;
              const config = preferences?.[reminder.key];
              
              // Skip if this specific reminder config doesn't exist
              if (!config) return null;

            return (
              <Card key={reminder.key} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Icon, Title, Description */}
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`p-3 rounded-xl ${reminder.bgColor}`}>
                        <Icon className={`h-6 w-6 ${reminder.color}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{reminder.title}</h3>
                        <p className="text-sm text-gray-600 mb-3">{reminder.description}</p>
                        
                        {/* Time & Frequency Settings */}
                        {config.enabled && (
                          <div className="space-y-3 mt-4">
                            {/* Time Picker */}
                            {!reminder.isMultiTime && (
                              <div className="flex items-center gap-3">
                                <Clock className="h-4 w-4 text-gray-500" />
                                <Label htmlFor={`${reminder.key}-time`} className="text-sm text-gray-700 min-w-[60px]">
                                  Time:
                                </Label>
                                <input
                                  id={`${reminder.key}-time`}
                                  type="time"
                                  value={config.time || '08:00'}
                                  onChange={(e) => handleTimeChange(reminder.key, e.target.value)}
                                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-healthloop-mint-500 focus:border-transparent"
                                />
                              </div>
                            )}

                            {/* Multiple Times for Water */}
                            {reminder.isMultiTime && config.times && (
                              <div className="flex items-start gap-3">
                                <Clock className="h-4 w-4 text-gray-500 mt-1" />
                                <div>
                                  <Label className="text-sm text-gray-700 mb-2 block">Reminder times:</Label>
                                  <div className="flex flex-wrap gap-2">
                                    {config.times.map((time, idx) => (
                                      <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-md font-medium">
                                        {time}
                                      </span>
                                    ))}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">5 reminders spread throughout the day</p>
                                </div>
                              </div>
                            )}

                            {/* Frequency Selector */}
                            {!reminder.isPeriodic && (
                              <div className="flex items-center gap-3">
                                <Calendar className="h-4 w-4 text-gray-500" />
                                <Label htmlFor={`${reminder.key}-frequency`} className="text-sm text-gray-700 min-w-[60px]">
                                  Frequency:
                                </Label>
                                <select
                                  id={`${reminder.key}-frequency`}
                                  value={config.frequency || 'daily'}
                                  onChange={(e) => handleFrequencyChange(reminder.key, e.target.value)}
                                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-healthloop-mint-500 focus:border-transparent"
                                >
                                  <option value="daily">Daily</option>
                                  <option value="every_2_days">Every 2 days</option>
                                  <option value="weekly">Weekly</option>
                                  <option value="off">Off</option>
                                </select>
                              </div>
                            )}

                            {/* Periodic Frequency */}
                            {reminder.isPeriodic && (
                              <div className="flex items-center gap-3">
                                <Calendar className="h-4 w-4 text-gray-500" />
                                <Label className="text-sm text-gray-700 min-w-[60px]">
                                  Frequency:
                                </Label>
                                <span className="text-sm text-gray-600 font-medium">
                                  {reminder.key === 'health_logging' ? 'Weekly (Sundays)' : 'Every 6 months'}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Toggle */}
                    <div className="flex flex-col items-end gap-2">
                      <Switch
                        id={`${reminder.key}-toggle`}
                        checked={config.enabled}
                        onCheckedChange={() => handleToggle(reminder.key)}
                        className="data-[state=checked]:bg-healthloop-mint-600"
                      />
                      <Label htmlFor={`${reminder.key}-toggle`} className="text-xs text-gray-600 cursor-pointer">
                        {config.enabled ? 'On' : 'Off'}
                      </Label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        )}

        {/* Save Button (Mobile) */}
        {preferences && Object.keys(preferences).length > 0 && (
          <div className="mt-6 sm:hidden">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-healthloop-mint-600 hover:bg-healthloop-mint-700"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        )}
      </main>

      <AppFooter />
    </div>
  );
};

export default RemindersSettings;
